import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import NotFound from "@/pages/NotFound";
import { LoadError } from "@/components/LoadError";
import { useLang } from "@/context/LanguageContext";
import { api, isNotFound } from "@/lib/api";
import { ISLAND_BY_PATH, islandUrl, tr } from "@/lib/translations";
import { LandingPage } from "@/components/LandingPage";
import { AdminEditButton } from "@/components/AdminEditButton";
import { hasBlocks, mergeBlocks } from "@/lib/landingBlocks";
import { PlacesBlock, IslandsBlock } from "@/components/LandingBlocks";
import { IslandProfile, IslandClimateTable } from "@/components/IslandExtras";
import { IslandOffers } from "@/components/IslandOffers";
import { OfflineNotice } from "@/components/OfflineNotice";
import { IslandGallery } from "@/components/IslandGallery";
import { IslandReviews, useIslandReviews, islandReviewsJsonLd } from "@/components/IslandReviews";
import CmsPage from "@/pages/CmsPage";
import { ComplexesSection } from "@/components/ComplexesSection";
import { IslandTips } from "@/components/IslandTips";

export default function IslandPage() {
  const { path } = useParams();
  const slug = ISLAND_BY_PATH[path];
  const { lang, t } = useLang();
  const [page, setPage] = useState(null);
  const [props, setProps] = useState([]);
  const [places, setPlaces] = useState([]);
  const reviews = useIslandReviews(slug);
  const [regions, setRegions] = useState([]);
  const [missing, setMissing] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setMissing(false);
    if (!slug) return;
    api.get(`/islands/${slug}`).then((r) => setPage(r.data)).catch((e) => (isNotFound(e) ? setMissing(true) : setFailed(true)));
    api.get("/properties", { params: { island: slug } }).then((r) => setProps(r.data)).catch(() => setProps([]));
    api.get("/places", { params: { island: slug } }).then((r) => setPlaces(r.data)).catch(() => setPlaces([]));
    api.get("/regions", { params: { island: slug } }).then((r) => setRegions(r.data.filter((x) => x.property_count > 0))).catch(() => setRegions([]));
    window.scrollTo(0, 0);
  }, [slug]);

  if (!slug) return <CmsPage />;
  if (failed) return <LoadError />;
  if (missing) return <NotFound />;
  if (!page) return <div className="max-w-7xl mx-auto px-6 py-32 min-h-screen text-center text-stone-500">{t.property.loading}</div>;
  const c = tr(page.content, lang);
  const name = page.name[lang] || page.name.en || page.name.de;
  const deSections = (page.content?.de || {}).sections;
  const placed = hasBlocks(deSections);
  // Blöcke „Orte“ / „Weitere Inseln“: standardmäßig unter den Objekten, oder vom Admin zwischen den Textabschnitten platziert (dann als H2, Orte-Auswahl möglich)
  const placesHeading = t.island.places.replace("{n}", name);

  return (
    <>
    <AdminEditButton kind="island" id={slug} />
    <OfflineNotice name={name} />
    <LandingPage
      testid="island-page" heroImage={page.hero_image} heroPosition={page.hero_position} heroAlt={tr(page.hero_alt, lang)} badge={t.island.badge} content={{ ...c, sections: mergeBlocks(c.sections, deSections) }} properties={props} island={slug}
      blocks={{ places: (s) => <PlacesBlock island={slug} places={places} slugs={s.slugs} heading={placesHeading} />, islands: () => <IslandsBlock island={slug} /> }}
      propertiesTitle={t.island.properties.replace("{n}", name)} ctaLabel={`${t.island.showAll.replace("{n}", name)} (${props.length})`} ctaTo={`/properties?island=${slug}`}
      facts={c.facts} statsName={name} breadcrumb={[{ name: t.nav.home, to: "/" }, { name, to: islandUrl(slug) }]}
      asideExtra={<><IslandProfile items={c.profile} name={name} lang={lang} /><div className="mt-4"><IslandOffers island={slug} properties={props} name={name} show={page.offers_show} max={page.offers_max} /></div></>}
      afterIntro={<><IslandClimateTable climate={page.climate} name={name} lang={lang} island={slug} compact /><IslandGallery images={page.gallery} alts={page.gallery_alts} name={name} lang={lang} /></>}
      afterProperties={<><ComplexesSection island={slug} heading={t.complex.onIsland.replace("{n}", name)} testId="island-complexes" /><IslandTips island={slug} name={name} /><IslandReviews data={reviews} slug={slug} name={name} lang={lang} /></>}
      afterIntroSection={regions.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-14" data-testid="island-regions">
          <div className="text-xs uppercase tracking-[0.3em] text-brand mb-3">{({ de: "Regionen", en: "Regions", es: "Regiones", nl: "Regio's", pl: "Regiony", fr: "Régions", it: "Regioni" })[lang] || "Regions"}</div>
          <div className="flex flex-wrap gap-2">
            {regions.map((r) => (
              <Link key={r.slug} to={`${islandUrl(slug)}/${r.slug}`} className="inline-flex items-center h-10 px-5 rounded-md bg-brand/75 hover:bg-brand text-white text-sm font-medium transition-colors" data-testid={`island-region-${r.slug}`}>
                {tr(r.name, lang)}{r.property_count > 0 && <span className="opacity-70 ml-1">({r.property_count})</span>}
              </Link>
            ))}
          </div>
        </section>
      )}
      jsonLd={{ "@context": "https://schema.org", "@type": "TouristDestination", name, description: c.meta_description, image: page.gallery?.length ? [page.hero_image, ...page.gallery].filter(Boolean) : page.hero_image, url: window.location.href, containedInPlace: { "@type": "Place", name: "Islas Canarias, España" },
        ...((c.profile || []).some((p) => (p.value || "").trim()) ? { additionalProperty: c.profile.filter((p) => (p.value || "").trim()).map((p) => ({ "@type": "PropertyValue", name: p.label, value: p.value })) } : {}),
        ...islandReviewsJsonLd(reviews, lang) }}
    >
      {!placed && <PlacesBlock island={slug} places={places} heading={placesHeading} placed={false} />}
      {!placed && <IslandsBlock island={slug} placed={false} />}
    </LandingPage>
    </>
  );
}
